import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { authPrivateApi } from "@/api/auth/authPrivateApi";
import { languagesApi } from "@/api/languages/languagesApi";
import { logout } from "./authSlice";
import {
  normalizeUiLanguageOptions,
  type BackendUiLanguage,
  type UiLanguageOption,
} from "@/shared/i18n/languages";

interface UiLanguagesState {
  options: UiLanguageOption[];
}

const initialState: UiLanguagesState = {
  options: [],
};

function readSchoolLanguages(payload: unknown): BackendUiLanguage[] {
  if (!payload || typeof payload !== "object") return [];

  const school = (payload as Record<string, unknown>).school;
  if (!school || typeof school !== "object" || Array.isArray(school)) return [];

  const languages = (school as Record<string, unknown>).languages;
  return Array.isArray(languages) ? (languages as BackendUiLanguage[]) : [];
}

export const uiLanguagesSlice = createSlice({
  name: "uiLanguages",
  initialState,
  reducers: {
    setUiLanguages: (
      state,
      action: PayloadAction<BackendUiLanguage[] | null | undefined>
    ) => {
      state.options = normalizeUiLanguageOptions(action.payload);
    },
    clearUiLanguages: (state) => {
      state.options = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(logout, (state) => {
      state.options = [];
    });

    builder.addMatcher(
      authPrivateApi.endpoints.getUser.matchFulfilled,
      (state, action) => {
        const nextOptions = normalizeUiLanguageOptions(
          readSchoolLanguages(action.payload)
        );

        if (nextOptions.length > 0 || state.options.length === 0) {
          state.options = nextOptions;
        }
      }
    );

    builder.addMatcher(
      languagesApi.endpoints.getSchoolLanguages.matchFulfilled,
      (state, action) => {
        state.options = normalizeUiLanguageOptions(action.payload);
      }
    );
  },
});

export const { setUiLanguages, clearUiLanguages } = uiLanguagesSlice.actions;
export default uiLanguagesSlice.reducer;
